import { useCallback, useState } from 'react';
import { v4 as createGuid } from 'uuid';

import { onSubmitFail } from 'src/components/Card/onSubmitFail';
import type { DirtyFormProps } from 'src/components/FormDrawer/FormDrawer';
import { useEnterReadMode } from 'src/components/Router/useEnterReadMode';
import { useId } from 'src/components/Router/useId';
import { usePage } from 'src/components/Router/usePage';
import { useAppConfig } from 'src/modules/appConfig';
import { useFocus } from 'src/modules/hooks/useFocus';
import type { ActionName } from 'src/types';

import { LanguageForm } from './LanguageForm';
import { useLanguages, useRemoveLanguage, useUpdateLanguage } from './data';
import type { LanguageUpdate } from './types';

export const EditLanguages = ({ onFormDirtyChange }: DirtyFormProps) => {
  const enterReadMode = useEnterReadMode();
  const { languages, loading } = useLanguages();
  const id = useId() || '';

  const activeLanguage = languages.find((language) => language.id === id);

  const [actionName] = useState<ActionName>(activeLanguage ? 'EDIT' : 'ADD');
  const isEdit = actionName === 'EDIT';
  const page = usePage();
  const { locale } = useAppConfig();

  const [updateLanguage] = useUpdateLanguage(actionName);

  const onSubmit = useCallback(
    ({ dirty, values }: { dirty: boolean; values: LanguageUpdate }) => {
      if (dirty) {
        const languageId = id || createGuid();
        updateLanguage({
          updateLanguageProficiencyInput: { ...values, id: languageId },
        });
      }
      enterReadMode();
    },
    [enterReadMode, id, updateLanguage],
  );

  const [removeLanguage] = useRemoveLanguage();
  const onDelete = useCallback(() => {
    enterReadMode();
    if (id) {
      removeLanguage({ deleteLanguageProficiencyInput: { id } });
    }
  }, [enterReadMode, id, removeLanguage]);

  const onSubmitFailFn = (errors: string[]) => {
    onSubmitFail(errors, page);
  };

  const languageRef = useFocus<HTMLInputElement>({
    card: 'languages',
    fieldName: 'language',
  });

  if (loading) {
    return null;
  }

  return (
    <LanguageForm
      locale={locale}
      initialValues={activeLanguage}
      onSubmit={onSubmit}
      onSubmitFail={onSubmitFailFn}
      onCancel={enterReadMode}
      scrollToTopOnMobile={false}
      onFormDirtyChange={onFormDirtyChange}
      inputRef={languageRef}
      {...(isEdit && { onDelete })}
    />
  );
};
